import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Space, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import AsyncReportDownloadModal, { dashboardAsyncReportLabelI18nKeys, useAsyncReportExportCooldown } from '../../../../../components/asyncReportDownload';
import {
    GENERATE_DSP_DELIVERY_COMPLETED_DETAIL_DOWNLOAD_REPORT,
    GenerateDspDeliveryCompletedDetailDownloadReportResponse,
    GenerateDspDeliveryCompletedDetailDownloadReportVariables,
} from '../graphql/mutations/generateDspDeliveryCompletedDetailDownloadReport';
import { DeliveryCompletedSummaryRecord } from './DeliveryCompletedSummaryTable';
import { useDspDeliveryCompletedDetailQuery } from '../graphql/hooks/useDspDeliveryCompletedDetailQuery';
import { DspDeliveryCompletedDetailFilterType, DspDeliveryCompletedDetailType } from '../graphql/queries/getDspDeliveryCompletedDetailList';

interface Props {
    open: boolean;
    onClose: () => void;
    record: DeliveryCompletedSummaryRecord | null;
    dateRange?: [string, string] | null;
}

const DspDeliveryCompletedDetailDrawer: React.FC<Props> = ({ open, onClose, record, dateRange }) => {
    const { t } = useTranslation();
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [generateDspDeliveryCompletedDetailDownload] = useMutation<
        GenerateDspDeliveryCompletedDetailDownloadReportResponse,
        GenerateDspDeliveryCompletedDetailDownloadReportVariables
    >(GENERATE_DSP_DELIVERY_COMPLETED_DETAIL_DOWNLOAD_REPORT);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const { records, loading, pagination, handleFilter } = useDspDeliveryCompletedDetailQuery();

    const rowKey = useCallback((r: DspDeliveryCompletedDetailType) => r.id, []);

    const deliveredDateStart = dateRange?.[0];
    const deliveredDateEnd = dateRange?.[1];
    
    const drawerTitle = useMemo(() => {
        const safe = (v: any) => (v === null || v === undefined || v === '' ? null : String(v));
        const deliveredDate = safe(record?.deliveredDate);
        const dateRangeText = deliveredDate
            ? deliveredDate
            : (deliveredDateStart && deliveredDateEnd ? `${deliveredDateStart} ~ ${deliveredDateEnd}` : null);

        const isDriverDrill = record?.drillLevel === 'driver';
        const routeId = record?.areaId === 0 ? null : record?.areaId;
        const route = isDriverDrill ? null : (safe(record?.areaName) || safe(routeId));
        const driver = safe(record?.driverName) || safe(record?.driverId);

        const parts = (
            isDriverDrill
                ? [dateRangeText, driver]
                : [dateRangeText, driver, route]
        ).filter(Boolean) as string[];
        return parts.join(' - ') || t('wms.delivery.deliveryCompletedSummary.detailTitle');
    }, [t, record, deliveredDateStart, deliveredDateEnd]);

    const buildDetailFilter = useCallback((): DspDeliveryCompletedDetailFilterType | null => {
        if (!record || !deliveredDateStart || !deliveredDateEnd) return null;
        const isDriverDrill = record.drillLevel === 'driver';
        const isHalfPriceOrder =
            record.drillMetric === 'discount' ? true : (record.drillMetric === 'standard' ? false : null);
        return {
            deliveredDateStart,
            deliveredDateEnd,
            deliveredDate: record.deliveredDate || null,
            hubId: isDriverDrill ? null : (record.hubId || null),
            warehouseId: isDriverDrill ? null : (record.warehouseId || null),
            areaId: isDriverDrill ? null : (record.areaId || null),
            driverId: record.driverId ?? null,
            isHalfPriceOrder,
        };
    }, [record, deliveredDateStart, deliveredDateEnd]);

    useEffect(() => {
        if (!open) {
            setSelectedRowKeys([]);
            return;
        }
        setSelectedRowKeys([]);
        const filter = buildDetailFilter();
        if (!filter) return;
        handleFilter(filter, 1, 10);
    }, [open, buildDetailFilter, handleFilter]);

    const columns: ColumnsType<DspDeliveryCompletedDetailType> = useMemo(
        () => [
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.trackingNumber'), dataIndex: 'trackingNumber', key: 'trackingNumber', width: 160, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.deliveredDate'), dataIndex: 'deliveredDate', key: 'deliveredDate', width: 120, render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.hubName'), dataIndex: 'hubName', key: 'hubName', width: 160, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.warehouseName'), dataIndex: 'warehouseName', key: 'warehouseName', width: 160, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.areaName'), dataIndex: 'areaName', key: 'areaName', width: 120, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.dspName'), dataIndex: 'dspName', key: 'dspName', width: 120, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.dspFullName'), dataIndex: 'dspFullName', key: 'dspFullName', width: 200, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.driverName'), dataIndex: 'driverName', key: 'driverName', width: 140, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.driverPhone'), dataIndex: 'driverPhone', key: 'driverPhone', width: 160, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.lastTaskDate'), dataIndex: 'lastTaskDate', key: 'lastTaskDate', width: 130, render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.standardizedAddress'), dataIndex: 'standardizedAddress', key: 'standardizedAddress', width: 320, render: (v: string) => v || '-' },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.isHalfPriceAddress'), dataIndex: 'isHalfPriceAddress', key: 'isHalfPriceAddress', width: 180, render: (v: boolean) => (v ? 'Y' : 'N') },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.deliverySequence'), dataIndex: 'deliverySequence', key: 'deliverySequence', width: 150, render: (v: number) => (v ?? '-') },
            { title: t('wms.delivery.deliveryCompletedSummary.detailColumns.isHalfPriceOrder'), dataIndex: 'isHalfPriceOrder', key: 'isHalfPriceOrder', width: 170, render: (v: boolean) => (v ? 'Y' : 'N') },
        ],
        [t]
    );

    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRowKeys.length) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const keySet = new Set(selectedRowKeys.map((k) => String(k)));
        const trackingNumbers = records
            .filter((rec) => keySet.has(String(rowKey(rec))))
            .map((rec) => (rec.trackingNumber ?? '').trim())
            .filter((tn): tn is string => Boolean(tn));
        if (!trackingNumbers.length) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (err) {
            console.error('Copy tracking numbers failed:', err);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRowKeys, records, rowKey, t]);

    const handleExportFullReport = useCallback(async () => {
        if (exportLoading) {
            message.warning(t('wms.delivery.deliveryCompletedSummary.exportGenerating'));
            return;
        }
        const filter = buildDetailFilter();
        if (!filter) {
            return;
        }
        if (pagination.totalElements === 0) {
            return;
        }

        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const response = await generateDspDeliveryCompletedDetailDownload({
                    variables: {
                        filter,
                        sortBy: 'deliveredDate',
                        sortDirection: 'DESC',
                        timezone: userTimezone,
                    },
                });
                const fileName = response.data?.generateDspDeliveryCompletedDetailDownloadReport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error(t('wms.delivery.deliveryCompletedSummary.exportFailed'));
                }
            } catch (error: unknown) {
                const err = error as {
                    graphQLErrors?: Array<{ message?: string; extensions?: { message?: string } }>;
                    message?: string;
                };
                if (err?.graphQLErrors?.length) {
                    message.error(
                        err.graphQLErrors[0].extensions?.message ?? err.graphQLErrors[0].message ?? t('wms.delivery.deliveryCompletedSummary.exportFailed')
                    );
                } else {
                    message.error(err?.message || t('wms.delivery.deliveryCompletedSummary.exportFailed'));
                }
            }
        });
    }, [
        buildDetailFilter,
        exportLoading,
        generateDspDeliveryCompletedDetailDownload,
        pagination.totalElements,
        runExportWithCooldown,
        t,
    ]);

    return (
        <>
        <Drawer
            title={
                <div className="flex items-center justify-start w-full flex-wrap">
                    <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">{drawerTitle}</span>
                </div>
            }
            placement="right"
            open={open}
            closable={false}
            onClose={onClose}
            width={1500}
            destroyOnClose
            extra={
                <Button
                    type="text"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                />
            }
        >
            <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
                {selectedRowKeys.length > 0 && (
                    <span className="text-sm text-gray-500">
                        {t('wms.delivery.deliveryCompletedSummary.selectedCount', { count: selectedRowKeys.length })}
                    </span>
                )}
                <Space>
                    <Button type="primary" onClick={copyTrackingNumbers}>
                        {t('common.buttons.copyTracking')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => void handleExportFullReport()}
                        loading={exportLoading}
                        disabled={exportLoading || pagination.totalElements === 0}
                    >
                        {getExportButtonLabel(t('components.button.download'))}
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={records}
                loading={loading}
                rowKey={rowKey}
                pagination={false}
                className="mb-4"
                scroll={{ x: 2020 }}
                size="small"
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                    preserveSelectedRowKeys: true,
                }}
            />
            <TablePaginationBar
                total={pagination.totalElements}
                current={pagination.page}
                pageSize={pagination.size}
                onChange={(page, pageSize) => {
                    const filter = buildDetailFilter();
                    if (!filter) return;
                    setSelectedRowKeys([]);
                    handleFilter(filter, page, pageSize);
                }}
                onShowSizeChange={(current, size) => {
                    const filter = buildDetailFilter();
                    if (!filter) return;
                    setSelectedRowKeys([]);
                    handleFilter(filter, current, size);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />
        </Drawer>
        <AsyncReportDownloadModal
            visible={downloadCompleteModalVisible}
            onClose={() => setDownloadCompleteModalVisible(false)}
            fileName={generatedFileName}
            reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.dspDeliveryCompletedDetailExport}
        />
        </>
    );
};

export default DspDeliveryCompletedDetailDrawer;

